/**
 * Tipos del estado del panel y logica pura que los transforma.
 *
 * Nada de aqui toca React ni la red: son funciones que reciben el estado actual
 * y devuelven uno nuevo, de modo que se puedan probar y reutilizar. Las
 * actualizaciones por SSE se apoyan en estas funciones para no recargar listas
 * completas ni duplicar mensajes.
 */
import type {
  AssigneeView,
  AuthUser,
  ConversationSummary,
  MembershipView,
  MessageView,
  SendMessageRequest,
} from '@shared/api-types.ts';
import { ADMIN_ROLES } from '@domain/types.ts';
import type {
  AccountStatus,
  AuthorType,
  BotMode,
  ConversationStatus,
  MemberRole,
  MessageStatus,
  MessageType,
} from '@domain/types.ts';
import { truncate } from './format.ts';

// --- Sesion ------------------------------------------------------------------

export type Session = {
  token: string;
  user: AuthUser;
  memberships: MembershipView[];
  tenantId: string;
};

export const STORAGE_TOKEN_KEY = 'wa.panel.token';
export const STORAGE_TENANT_KEY = 'wa.panel.tenant';

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readStoredSession(): { token: string; tenantId: string } | null {
  const storage = safeStorage();
  if (!storage) return null;
  const token = storage.getItem(STORAGE_TOKEN_KEY);
  if (!token) return null;
  return { token, tenantId: storage.getItem(STORAGE_TENANT_KEY) ?? '' };
}

export function storeSession(token: string, tenantId: string): void {
  const storage = safeStorage();
  if (!storage) return;
  storage.setItem(STORAGE_TOKEN_KEY, token);
  storage.setItem(STORAGE_TENANT_KEY, tenantId);
}

export function clearStoredSession(): void {
  const storage = safeStorage();
  if (!storage) return;
  storage.removeItem(STORAGE_TOKEN_KEY);
  storage.removeItem(STORAGE_TENANT_KEY);
}

/** Organizacion activa: la guardada si sigue siendo valida; si no, la primera. */
export function pickTenantId(memberships: MembershipView[], preferred: string | null): string {
  if (memberships.length === 0) return '';
  const match = memberships.find((membership) => membership.tenantId === preferred);
  return match ? match.tenantId : (memberships[0] as MembershipView).tenantId;
}

// --- Rutas (enrutado propio sobre el hash) -----------------------------------

export type Route =
  | { name: 'inbox'; conversationId: string | null }
  | { name: 'metrics' }
  | { name: 'ai' }
  | { name: 'knowledge' }
  | { name: 'team' }
  | { name: 'contacts' }
  | { name: 'accounts' }
  | { name: 'templates' }
  | { name: 'apiKeys' }
  | { name: 'tenants' };

export const DEFAULT_ROUTE: Route = { name: 'inbox', conversationId: null };

export function parseRoute(hash: string): Route {
  const clean = hash.replace(/^#\/?/, '').trim();
  const [segment = '', param = ''] = clean.split('/');
  switch (segment) {
    case 'metrics':
      return { name: 'metrics' };
    case 'ai':
      return { name: 'ai' };
    case 'knowledge':
      return { name: 'knowledge' };
    case 'team':
      return { name: 'team' };
    case 'contacts':
      return { name: 'contacts' };
    case 'accounts':
      return { name: 'accounts' };
    case 'templates':
      return { name: 'templates' };
    case 'api-keys':
      return { name: 'apiKeys' };
    case 'tenants':
      return { name: 'tenants' };
    case 'inbox':
      return { name: 'inbox', conversationId: param ? decodeURIComponent(param) : null };
    default:
      return DEFAULT_ROUTE;
  }
}

export function routeHash(route: Route): string {
  switch (route.name) {
    case 'inbox':
      return route.conversationId ? `#/inbox/${encodeURIComponent(route.conversationId)}` : '#/inbox';
    case 'metrics':
      return '#/metrics';
    case 'ai':
      return '#/ai';
    case 'knowledge':
      return '#/knowledge';
    case 'team':
      return '#/team';
    case 'contacts':
      return '#/contacts';
    case 'accounts':
      return '#/accounts';
    case 'templates':
      return '#/templates';
    case 'apiKeys':
      return '#/api-keys';
    case 'tenants':
      return '#/tenants';
  }
}

// --- Filtros de la bandeja ---------------------------------------------------

export type AssigneeFilter = 'all' | 'me' | 'unassigned';
export type StatusFilter = ConversationStatus | 'all';

export type InboxFilters = {
  status: StatusFilter;
  assignee: AssigneeFilter;
  accountId: string | 'all';
  q: string;
};

export const DEFAULT_FILTERS: InboxFilters = {
  status: 'open',
  assignee: 'all',
  accountId: 'all',
  q: '',
};

export const STATUS_FILTER_OPTIONS: StatusFilter[] = [
  'open',
  'pending',
  'resolved',
  'snoozed',
  'all',
];

export const CONVERSATION_PAGE_SIZE = 25;
export const MESSAGE_PAGE_SIZE = 40;
/** Tamano de pagina de contactos: el maximo que admite la API es 200. */
export const CONTACTS_PAGE_SIZE = 50;

/** ¿Este resumen pertenece a la vista filtrada actual? */
export function matchesFilters(
  summary: ConversationSummary,
  filters: InboxFilters,
  currentUserId: string,
): boolean {
  if (filters.status !== 'all' && summary.status !== filters.status) return false;
  if (filters.accountId !== 'all' && summary.account.id !== filters.accountId) return false;
  if (filters.assignee === 'me' && summary.assignee?.id !== currentUserId) return false;
  if (filters.assignee === 'unassigned' && summary.assignee !== null) return false;

  const query = filters.q.trim().toLowerCase();
  if (!query) return true;
  const haystack = [
    summary.contact.profileName ?? '',
    summary.contact.waId,
    summary.lastMessagePreview ?? '',
    summary.tags.join(' '),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

// --- Conversaciones ----------------------------------------------------------

/** Mas recientes primero; las conversaciones sin mensajes van al final. */
export function sortConversations(list: ConversationSummary[]): ConversationSummary[] {
  return [...list].sort((a, b) => {
    const left = a.lastMessageAt ? Date.parse(a.lastMessageAt) : 0;
    const right = b.lastMessageAt ? Date.parse(b.lastMessageAt) : 0;
    if (left === right) return a.id.localeCompare(b.id);
    return right - left;
  });
}

/** Anade o reemplaza una conversacion sin tocar el resto de la lista. */
export function upsertConversation(
  list: ConversationSummary[],
  incoming: ConversationSummary,
): ConversationSummary[] {
  const index = list.findIndex((item) => item.id === incoming.id);
  if (index === -1) return sortConversations([...list, incoming]);
  const next = [...list];
  next[index] = incoming;
  return sortConversations(next);
}

/** Cambia solo el asignado de una conversacion de la lista. */
export function patchConversationAssignee(
  list: ConversationSummary[],
  conversationId: string,
  assignee: AssigneeView | null,
): ConversationSummary[] {
  let changed = false;
  const next = list.map((item) => {
    if (item.id !== conversationId) return item;
    changed = true;
    return { ...item, assignee };
  });
  return changed ? next : list;
}

/** Adelanta la vista previa tras enviar un mensaje, sin esperar al SSE. */
export function touchConversationPreview(
  list: ConversationSummary[],
  conversationId: string,
  preview: string,
  at: string,
): ConversationSummary[] {
  const index = list.findIndex((item) => item.id === conversationId);
  if (index === -1) return list;
  const next = [...list];
  const current = next[index] as ConversationSummary;
  next[index] = {
    ...current,
    lastMessageAt: at,
    lastMessagePreview: truncate(preview, 120),
    lastMessageDirection: 'outbound',
  };
  return sortConversations(next);
}

/** El agente ha abierto la conversacion: se limpia el contador de no leidos. */
export function clearConversationUnread(
  list: ConversationSummary[],
  conversationId: string,
): ConversationSummary[] {
  const index = list.findIndex((item) => item.id === conversationId);
  if (index === -1) return list;
  const current = list[index] as ConversationSummary;
  if (current.unreadCount === 0) return list;
  const copy = [...list];
  copy[index] = { ...current, unreadCount: 0 };
  return copy;
}

// --- Mensajes ----------------------------------------------------------------

export const OPTIMISTIC_ID_PREFIX = 'local-';

let optimisticCounter = 0;

export function newOptimisticId(): string {
  optimisticCounter += 1;
  return `${OPTIMISTIC_ID_PREFIX}${Date.now().toString(36)}-${optimisticCounter}`;
}

export function isOptimistic(message: MessageView): boolean {
  return message.id.startsWith(OPTIMISTIC_ID_PREFIX);
}

/** Orden cronologico ascendente, estable ante fechas identicas. */
export function sortMessages(list: MessageView[]): MessageView[] {
  return [...list].sort((a, b) => {
    const left = Date.parse(a.createdAt);
    const right = Date.parse(b.createdAt);
    if (left === right) return 0;
    return left - right;
  });
}

/**
 * Incorpora un mensaje al hilo sin duplicarlo.
 *
 * Un mismo mensaje puede llegar por SSE y por la respuesta HTTP del envio, de
 * modo que se reconcilia por `id`, por `clientMessageId` y, si el servidor no
 * devolviese ninguno de los dos, sustituyendo la burbuja optimista pendiente
 * que tenga el mismo texto.
 */
export function mergeMessage(list: MessageView[], incoming: MessageView): MessageView[] {
  const byId = list.findIndex((item) => item.id === incoming.id);
  if (byId !== -1) {
    const next = [...list];
    next[byId] = incoming;
    return sortMessages(next);
  }

  if (incoming.clientMessageId) {
    const byClientId = list.findIndex(
      (item) => item.clientMessageId !== null && item.clientMessageId === incoming.clientMessageId,
    );
    if (byClientId !== -1) {
      const next = [...list];
      next[byClientId] = incoming;
      return sortMessages(next);
    }
  }

  if (incoming.direction === 'outbound') {
    const pending = list.findIndex(
      (item) =>
        isOptimistic(item) &&
        item.status === 'sending' &&
        item.direction === 'outbound' &&
        item.text !== null &&
        item.text === incoming.text,
    );
    if (pending !== -1) {
      const next = [...list];
      next[pending] = incoming;
      return sortMessages(next);
    }
  }

  return sortMessages([...list, incoming]);
}

/** Sustituye la burbuja optimista por el mensaje real que devuelve la API. */
export function replaceOptimistic(
  list: MessageView[],
  optimisticId: string,
  incoming: MessageView,
): MessageView[] {
  const withoutOptimistic = list.filter((item) => item.id !== optimisticId);
  return mergeMessage(withoutOptimistic, incoming);
}

/** Marca como fallida la burbuja optimista cuando el envio no sale adelante. */
export function failOptimistic(
  list: MessageView[],
  optimisticId: string,
  errorTitle: string,
  errorDetail: string | null,
): MessageView[] {
  return list.map((item) =>
    item.id === optimisticId ? { ...item, status: 'failed', errorTitle, errorDetail } : item,
  );
}

/** Actualiza el tick de entrega de un mensaje concreto (evento `message.status`). */
export function applyMessageStatus(
  list: MessageView[],
  messageId: string,
  status: MessageStatus,
  error?: string | null,
  errorCode?: string | null,
  errorTitle?: string | null,
): MessageView[] {
  let changed = false;
  const next = list.map((item) => {
    if (item.id !== messageId) return item;
    changed = true;
    const timestamps: Partial<MessageView> = {};
    const now = new Date().toISOString();
    if (status === 'sent' && !item.sentAt) timestamps.sentAt = now;
    if (status === 'delivered' && !item.deliveredAt) timestamps.deliveredAt = now;
    if (status === 'read' && !item.readAt) timestamps.readAt = now;
    return {
      ...item,
      ...timestamps,
      status,
      // El evento ya trae el codigo y el titulo traducido, igual que la API REST:
      // no hace falta volver a pedir el mensaje para poder explicar el fallo.
      errorCode: status === 'failed' ? (errorCode ?? item.errorCode) : null,
      errorTitle:
        status === 'failed' ? (errorTitle ?? item.errorTitle ?? 'El mensaje no se pudo enviar') : null,
      errorDetail: status === 'failed' ? (error ?? item.errorDetail) : item.errorDetail,
    };
  });
  return changed ? next : list;
}

export type OptimisticAuthor = { id: string; name: string };

/** Burbuja local que se muestra mientras el mensaje viaja al servidor. */
export function createOptimisticMessage(
  id: string,
  request: SendMessageRequest,
  author: OptimisticAuthor,
): MessageView {
  const base: Omit<MessageView, 'type' | 'text' | 'media' | 'template'> = {
    id,
    direction: 'outbound',
    authorType: 'user',
    authorUserId: author.id,
    authorName: author.name,
    wamid: null,
    clientMessageId: null,
    reply: null,
    status: 'sending',
    errorCode: null,
    errorTitle: null,
    errorDetail: null,
    createdAt: new Date().toISOString(),
    sentAt: null,
    deliveredAt: null,
    readAt: null,
  };

  switch (request.kind) {
    case 'text':
      return { ...base, type: 'text', text: request.text, media: null, template: null };
    case 'template':
      return {
        ...base,
        type: 'template',
        text: null,
        media: null,
        template: { name: request.templateName, language: request.language },
      };
    case 'media':
      return {
        ...base,
        type: request.mediaKind,
        text: null,
        media: {
          id: request.mediaId,
          caption: request.caption,
          filename: request.filename,
        },
        template: null,
      };
    case 'interactive':
      return { ...base, type: 'interactive', text: request.body, media: null, template: null };
  }
}

/** Texto corto del mensaje para la vista previa de la lista. */
export function messagePreview(message: MessageView): string {
  switch (message.type) {
    case 'text':
      return truncate(message.text ?? '', 120);
    case 'image':
      return message.media?.caption ? `Imagen: ${truncate(message.media.caption, 100)}` : 'Imagen';
    case 'audio':
      return 'Audio';
    case 'video':
      return 'Video';
    case 'document':
      return message.media?.filename ? `Documento: ${message.media.filename}` : 'Documento';
    case 'sticker':
      return 'Sticker';
    case 'location':
      return 'Ubicacion';
    case 'contacts':
      return 'Contacto compartido';
    case 'interactive':
      return 'Mensaje interactivo';
    case 'button':
      return message.text ? `Boton: ${message.text}` : 'Boton pulsado';
    case 'template':
      return message.template ? `Plantilla: ${message.template.name}` : 'Plantilla';
    case 'reaction':
      return 'Reaccion';
    case 'system':
      return message.text ?? 'Aviso del sistema';
    case 'unsupported':
      return 'Mensaje no soportado';
  }
}

// --- Etiquetas de la interfaz ------------------------------------------------

export const BOT_MODE_LABELS: Record<BotMode, string> = {
  auto: 'IA activa',
  paused: 'Atiende una persona',
  off: 'IA desactivada',
};

export const BOT_MODE_HINTS: Record<BotMode, string> = {
  auto: 'El agente de IA responde y escala a una persona si hace falta.',
  paused: 'Un agente humano ha tomado el control; la IA permanece en silencio.',
  off: 'La IA no interviene en esta conversacion.',
};

export const MESSAGE_TYPE_LABELS: Record<MessageType, string> = {
  text: 'Texto',
  image: 'Imagen',
  audio: 'Audio',
  video: 'Video',
  document: 'Documento',
  sticker: 'Sticker',
  location: 'Ubicacion',
  contacts: 'Contacto',
  interactive: 'Interactivo',
  button: 'Boton',
  template: 'Plantilla',
  reaction: 'Reaccion',
  system: 'Sistema',
  unsupported: 'No soportado',
};

export const AUTHOR_TYPE_LABELS: Record<AuthorType, string> = {
  contact: 'Cliente',
  user: 'Agente',
  bot: 'Asistente de IA',
  system: 'Sistema',
};

export const MESSAGE_STATUS_LABELS: Record<MessageStatus, string> = {
  received: 'Recibido',
  queued: 'En cola',
  sending: 'Enviando',
  sent: 'Enviado',
  delivered: 'Entregado',
  read: 'Leido',
  failed: 'Fallido',
};

export const ACCOUNT_STATUS_LABELS: Record<AccountStatus, string> = {
  active: 'Activa',
  disabled: 'Desactivada',
};

export const OPT_IN_LABELS: Record<string, string> = {
  unknown: 'Sin confirmar',
  opted_in: 'Ha aceptado',
  opted_out: 'Ha rechazado',
};

export const WEEKDAY_LABELS = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];

export function conversationDisplayName(summary: {
  contact: { profileName: string | null; waId: string };
}): string {
  return summary.contact.profileName?.trim() || summary.contact.waId;
}

/** Rol de la persona en la organizacion activa (o `null` si no es miembro). */
export function activeRole(session: {
  tenantId: string;
  memberships: MembershipView[];
}): MemberRole | null {
  return session.memberships.find((item) => item.tenantId === session.tenantId)?.role ?? null;
}

/**
 * La API exige rol de administrador para escribir la configuracion del agente,
 * la base de conocimiento, el equipo y las cuentas. La lectura esta abierta a
 * cualquier miembro, asi que el panel oculta o deshabilita solo la escritura.
 */
export function canManageSettings(role: MemberRole | null | undefined): boolean {
  return role !== null && role !== undefined && ADMIN_ROLES.includes(role);
}

